import * as m from '../paraglide/messages.js';

export const STUDIO_INSPECT_REQUEST_TYPE = 'stimcar-studio-inspect';
export const STUDIO_INSPECT_READY_TYPE = 'stimcar-studio-inspect-ready';
export const STUDIO_INSPECT_SELECTION_TYPE = 'stimcar-studio-selection';
export const STUDIO_INSPECT_HOVER_TYPE = 'stimcar-studio-inspect-hover';
export const STUDIO_INSPECT_PICK_TYPE = 'stimcar-studio-inspect-pick';
export const STUDIO_SCREENSHOT_BOUNDS_REQUEST_TYPE = 'stimcar-studio-screenshot-bounds';
export const STUDIO_SCREENSHOT_BOUNDS_RESULT_TYPE = 'stimcar-studio-screenshot-bounds-result';
export const STUDIO_SCREENSHOT_REQUEST_TYPE = 'stimcar-studio-screenshot';
export const STUDIO_SCREENSHOT_RESULT_TYPE = 'stimcar-studio-screenshot-result';
export const STUDIO_LOCATION_REQUEST_TYPE = 'stimcar-studio-location';
export const STUDIO_LOCATION_RESULT_TYPE = 'stimcar-studio-location-result';
export const STUDIO_LOCATION_PUSH_TYPE = 'stimcar-studio-location-push';

export type StudioScreenshotMode = 'viewport' | 'selection';

export type StudioInspectSelectionRect = {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
};

export type StudioInspectSelection = {
  readonly tag: string;
  readonly id: string | undefined;
  readonly className: string | undefined;
  readonly text: string;
  readonly selector: string;
  readonly path: string;
  /** Element box relative to the preview iframe viewport (for cropped screenshots). */
  readonly rect?: StudioInspectSelectionRect;
  /** Message key from closest `[data-studio-key]` when present. */
  readonly messageKey?: string;
  /** Source file hint from closest `[data-studio-file]` when present. */
  readonly sourceFile?: string;
};

export type StudioInspectRequest = {
  readonly type: typeof STUDIO_INSPECT_REQUEST_TYPE;
  readonly enabled: boolean;
};

export function studioInspectRequest(enabled: boolean): StudioInspectRequest {
  return { type: STUDIO_INSPECT_REQUEST_TYPE, enabled };
}

/** Target origin for postMessage into a preview iframe (never `*`). */
export function previewPostMessageOrigin(previewUrl: string): string | undefined {
  if (previewUrl.trim() === '') {
    return undefined;
  }
  return new URL(previewUrl).origin;
}

export function postToPreviewFrame(
  iframe: HTMLIFrameElement | null,
  payload: unknown,
  previewUrl: string
): void {
  const frame = iframe?.contentWindow;
  const targetOrigin = previewPostMessageOrigin(previewUrl);
  if (frame === undefined || frame === null || targetOrigin === undefined) {
    return;
  }
  frame.postMessage(payload, targetOrigin);
}

export function isPreviewMessageOrigin(eventOrigin: string, previewUrl: string): boolean {
  const expected = previewPostMessageOrigin(previewUrl);
  return expected !== undefined && eventOrigin === expected;
}

export type StudioInspectPoint = {
  readonly type: typeof STUDIO_INSPECT_HOVER_TYPE | typeof STUDIO_INSPECT_PICK_TYPE;
  readonly x: number;
  readonly y: number;
};

export function studioInspectPoint(
  type: StudioInspectPoint['type'],
  x: number,
  y: number
): StudioInspectPoint {
  return { type, x, y };
}

export type InspectClientRect = {
  readonly left: number;
  readonly top: number;
};

export type InspectClientPoint = {
  readonly x: number;
  readonly y: number;
};

export function inspectPointFromClient(
  iframeRect: InspectClientRect,
  clientX: number,
  clientY: number
): InspectClientPoint {
  return { x: clientX - iframeRect.left, y: clientY - iframeRect.top };
}

export function isStudioInspectPoint(
  value: unknown,
  type: StudioInspectPoint['type']
): value is StudioInspectPoint {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return record.type === type && typeof record.x === 'number' && typeof record.y === 'number';
}

export function isStudioInspectReady(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return record.type === STUDIO_INSPECT_READY_TYPE;
}

export function isStudioInspectSelection(value: unknown): value is StudioInspectSelection {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  if (
    typeof record.tag !== 'string' ||
    typeof record.selector !== 'string' ||
    typeof record.path !== 'string' ||
    typeof record.text !== 'string'
  ) {
    return false;
  }
  if (record.messageKey !== undefined && typeof record.messageKey !== 'string') {
    return false;
  }
  if (record.sourceFile !== undefined && typeof record.sourceFile !== 'string') {
    return false;
  }
  if (record.rect === undefined) {
    return true;
  }
  return isStudioInspectSelectionRect(record.rect);
}

export function isStudioInspectSelectionRect(value: unknown): value is StudioInspectSelectionRect {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    typeof record.left === 'number' &&
    typeof record.top === 'number' &&
    typeof record.width === 'number' &&
    typeof record.height === 'number' &&
    record.width > 0 &&
    record.height > 0
  );
}

/** Last selection that includes a measurable rect, if any. */
export function lastSelectionWithRect(
  selections: readonly StudioInspectSelection[]
): StudioInspectSelection | undefined {
  const withRect = selections.filter((item) => item.rect !== undefined);
  if (withRect.length === 0) {
    return undefined;
  }
  return withRect[withRect.length - 1];
}

export function lastInspectSelection(
  selections: readonly StudioInspectSelection[]
): StudioInspectSelection | undefined {
  if (selections.length === 0) {
    return undefined;
  }
  return selections[selections.length - 1];
}

export type StudioScreenshotBoundsRequest = {
  readonly type: typeof STUDIO_SCREENSHOT_BOUNDS_REQUEST_TYPE;
  readonly requestId: string;
  readonly selector: string | undefined;
};

export function studioScreenshotBoundsRequest(
  requestId: string,
  selector: string | undefined
): StudioScreenshotBoundsRequest {
  return { type: STUDIO_SCREENSHOT_BOUNDS_REQUEST_TYPE, requestId, selector };
}

export type StudioScreenshotBoundsResult = {
  readonly type: typeof STUDIO_SCREENSHOT_BOUNDS_RESULT_TYPE;
  readonly requestId: string;
  readonly ok: boolean;
  readonly rect: StudioInspectSelectionRect | undefined;
};

export function isStudioScreenshotBoundsResult(
  value: unknown
): value is StudioScreenshotBoundsResult {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  if (
    record.type !== STUDIO_SCREENSHOT_BOUNDS_RESULT_TYPE ||
    typeof record.requestId !== 'string' ||
    typeof record.ok !== 'boolean'
  ) {
    return false;
  }
  if (record.ok !== true) {
    return true;
  }
  return isStudioInspectSelectionRect(record.rect);
}

export async function requestPreviewSelectionBounds(
  iframe: HTMLIFrameElement,
  selector: string | undefined,
  previewUrl: string,
  timeoutMs: number = 1_000
): Promise<StudioInspectSelectionRect | undefined> {
  const frame = iframe.contentWindow;
  const targetOrigin = previewPostMessageOrigin(previewUrl);
  if (frame === null || targetOrigin === undefined) {
    return undefined;
  }
  const requestId = crypto.randomUUID();
  return await new Promise((resolve) => {
    let settled = false;
    function finish(rect: StudioInspectSelectionRect | undefined): void {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      resolve(rect);
    }
    function onMessage(event: MessageEvent): void {
      if (!isPreviewMessageOrigin(event.origin, previewUrl)) {
        return;
      }
      if (typeof event.data !== 'object' || event.data === null) {
        return;
      }
      if (!isStudioScreenshotBoundsResult(event.data)) {
        return;
      }
      if (event.data.requestId !== requestId) {
        return;
      }
      finish(event.data.ok ? event.data.rect : undefined);
    }
    window.addEventListener('message', onMessage);
    frame.postMessage(studioScreenshotBoundsRequest(requestId, selector), targetOrigin);
    const timer = window.setTimeout(() => {
      finish(undefined);
    }, timeoutMs);
  });
}

export type StudioLocationRequest = {
  readonly type: typeof STUDIO_LOCATION_REQUEST_TYPE;
  readonly requestId: string;
};

export function studioLocationRequest(requestId: string): StudioLocationRequest {
  return { type: STUDIO_LOCATION_REQUEST_TYPE, requestId };
}

export type StudioLocationResult = {
  readonly type: typeof STUDIO_LOCATION_RESULT_TYPE;
  readonly requestId: string;
  readonly path: string;
};

export function isStudioLocationResult(value: unknown): value is StudioLocationResult {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    record.type === STUDIO_LOCATION_RESULT_TYPE &&
    typeof record.requestId === 'string' &&
    typeof record.path === 'string'
  );
}

export type StudioLocationPush = {
  readonly type: typeof STUDIO_LOCATION_PUSH_TYPE;
  readonly path: string;
};

export function isStudioLocationPush(value: unknown): value is StudioLocationPush {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return record.type === STUDIO_LOCATION_PUSH_TYPE && typeof record.path === 'string';
}

/**
 * Ask the preview iframe for its current pathname + search + hash (cross-origin safe).
 * Returns undefined when the bridge is missing or the request times out.
 */
export async function requestPreviewLocation(
  iframe: HTMLIFrameElement,
  previewUrl: string,
  timeoutMs: number = 500
): Promise<string | undefined> {
  const frame = iframe.contentWindow;
  const targetOrigin = previewPostMessageOrigin(previewUrl);
  if (frame === null || targetOrigin === undefined) {
    return undefined;
  }
  try {
    const loc = frame.location;
    if (loc.origin === targetOrigin) {
      return `${loc.pathname}${loc.search}${loc.hash}`;
    }
  } catch {
    // Cross-origin: fall through to postMessage.
  }
  const requestId = crypto.randomUUID();
  return await new Promise((resolve) => {
    let settled = false;
    function finish(path: string | undefined): void {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      resolve(path);
    }
    function onMessage(event: MessageEvent): void {
      if (!isPreviewMessageOrigin(event.origin, previewUrl)) {
        return;
      }
      if (!isStudioLocationResult(event.data)) {
        return;
      }
      if (event.data.requestId !== requestId) {
        return;
      }
      finish(event.data.path);
    }
    window.addEventListener('message', onMessage);
    frame.postMessage(studioLocationRequest(requestId), targetOrigin);
    const timer = window.setTimeout(() => {
      finish(undefined);
    }, timeoutMs);
  });
}

export type StudioScreenshotRequest = {
  readonly type: typeof STUDIO_SCREENSHOT_REQUEST_TYPE;
  readonly requestId: string;
  readonly mode: StudioScreenshotMode;
  readonly selector: string | undefined;
};

export function studioScreenshotRequest(
  requestId: string,
  mode: StudioScreenshotMode,
  selector: string | undefined
): StudioScreenshotRequest {
  return { type: STUDIO_SCREENSHOT_REQUEST_TYPE, requestId, mode, selector };
}

export type StudioScreenshotResult = {
  readonly type: typeof STUDIO_SCREENSHOT_RESULT_TYPE;
  readonly requestId: string;
  readonly ok: boolean;
  readonly dataUrl: string | undefined;
};

export function isStudioScreenshotResult(value: unknown): value is StudioScreenshotResult {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  if (
    record.type !== STUDIO_SCREENSHOT_RESULT_TYPE ||
    typeof record.requestId !== 'string' ||
    typeof record.ok !== 'boolean'
  ) {
    return false;
  }
  if (record.ok !== true) {
    return true;
  }
  return typeof record.dataUrl === 'string' && record.dataUrl.startsWith('data:image/');
}

export function inspectSelectionLabel(selection: StudioInspectSelection): string {
  if (selection.messageKey !== undefined && selection.messageKey !== '') {
    const quoted = selection.text !== '' ? ` “${selection.text}”` : '';
    return `${selection.messageKey}${quoted}`;
  }
  if (selection.text !== '') {
    return `${selection.selector} “${selection.text}”`;
  }
  return selection.selector;
}

export function inspectSelectionPromptLine(selection: StudioInspectSelection): string {
  if (selection.messageKey !== undefined && selection.messageKey !== '') {
    const filePart =
      selection.sourceFile !== undefined && selection.sourceFile !== ''
        ? ` file=${selection.sourceFile}`
        : '';
    const textPart = selection.text !== '' ? ` “${selection.text}”` : '';
    return m.inspectSelectionWithKey({
      messageKey: selection.messageKey,
      filePart,
      textPart,
    });
  }
  return `- ${selection.path} (${selection.selector})${selection.text !== '' ? ` “${selection.text}”` : ''}`;
}

export function inspectSelectionsPrompt(selections: readonly StudioInspectSelection[]): string {
  if (selections.length === 0) {
    return '';
  }
  const lines = selections.map((item) => inspectSelectionPromptLine(item));
  return `${m.inspectSelectionsHeader()}\n${lines.join('\n')}`;
}

export function mergeComposerPrompt(
  draft: string,
  selections: readonly StudioInspectSelection[]
): string {
  const selected = inspectSelectionsPrompt(selections);
  const text = draft.trim();
  if (selected === '') {
    return text;
  }
  if (text === '') {
    return selected;
  }
  return `${selected}\n\n${text}`;
}
